//#region Libraries
import { useState } from "react";
//#endregion
//#region Styles
import { Container } from "./styles";
import { Row } from "components/styles";
//#endregion
//#region Components
import HelmetUTP from "components/HelmetUTP/HelmetUTP";
import DataHeaderDetail from "components/DataHeaderDetail/DataHeaderDetail";
import BackToView from "components/BackToView/BackToView";
import CustomButton from "components/CustomButton/CustomButton";
import CustomTableList from "components/CustomTableList/CustomTableList";
import TableListItem from "./components/TableListItem/TableListItem";
import FormRole from "./components/FormRole/FormRole";
import Preloader from "components/Preloader/Preloader";
import HeaderRoles from "./components/HeaderRoles/HeaderRoles";
import ToastMessage from "components/ToastMessage/ToastMessage";
import DialogAlertAction from "components/DialogAlertAction/DialogAlertAction";
//#endregion
//#region Icons
import PeopleIcon from "icons/PeopleIcon";
import AddingPersonIcon from "icons/AddingPersonIcon";
//#endregion
//#region Services
//#endregion
//#region Utils
import ABSOLUTE_PATHS from "routes/utils/absolutePaths";
import useUserList from "./utils/hooks/useUserList";
import { HEADERS_TABLE_LIST, INIT_FILTER_FIELDS } from "./utils/constants";
import useDataRelatedToRoles from "./utils/hooks/useDataRelatedToRoles";
import { requestDeleteUser } from "services/settings/roleAdmin/aboutRoleAdmin";
import useMainContext from "utils/context/useMainContext";
import useLoadingRequest from "components/Preloader/utils/hooks/useLoadingRequest";
import usePaginator from "components/Paginator/utils/hooks/usePaginator";
//#endregion

const SettingsRoleAdmin = () => {
    const mainContext = useMainContext();
    //#region States
    const [filterFields, setFilterFields] = useState({ ...INIT_FILTER_FIELDS });
    const [showForm, setShowForm] = useState(false);
    const [currentUser, setCurrentUser] = useState(null);
    const loadingRequest = useLoadingRequest();
    const [toastMessage, setToastMessage] = useState({
        message: "-",
        hidden: true
    });
    // eslint-disable-next-line no-unused-vars
    const [fatalError, setFatalError] = useState(false);
    const [showDialogDeleteUser, setShowDialogDeleteUser] = useState(false);
    //#endregion
    const paginator = usePaginator();
    const dataRelatedToRoles = useDataRelatedToRoles({
        setFatalError,
        loadingRequest
    });
    const { userList, searchedFilters } = useUserList({
        filterFields,
        notifications: {
            loadingRequest,
            setToastMessage
        },
        paginator
    });
    //#region Effects
    //#endregion
    //#region Functions
    const toggleShowDialogDeleteUser = (value = null) => {
        setShowDialogDeleteUser(value !== null ? value : !showDialogDeleteUser);
    }
    const toggleShowForm = () => {
        setShowForm(prev => !prev);
    }
    const openForm = (user = null) => {
        setCurrentUser(user);
        toggleShowForm();
    }
    const closeForm = () => {
        setCurrentUser(null);
        toggleShowForm();
    }
    const checkClearedFilterFields = () => {
        const clearedFilterFields = Object.keys(filterFields).every(key => {
            return filterFields[key] === INIT_FILTER_FIELDS[key];
        });
        return clearedFilterFields;
    }
    const clearFilterFields = () => {
        setFilterFields({ ...INIT_FILTER_FIELDS });
        userList.triggerFillRequest(true);
    }
    const openDialogForDeleteUser = user => {
        toggleShowDialogDeleteUser(true);
        setCurrentUser(user);
    }
    const deleteUser = async () => {
        loadingRequest.show("Eliminando usuario...");
        // Realizando petición de creación o actualización
        const result = await requestDeleteUser(currentUser.user);
        // Gestionando respuesta
        if (result.success) {
            // Exitosa
            toggleShowDialogDeleteUser(false);
            userList.fill("¡Se eliminó exitosamente el Usuario!");
            return;
        }
        // Fallida
        loadingRequest.hide();
        mainContext.setShowDialogUnexpectedError(true);
    }
    const clearNewlyAddedOrUpdatedUser = () => {
        userList.newlyAddedOrUpdatedUser.set(null);
    }
    //#endregion
    return (
        <>
        <HelmetUTP title="Admin. de roles"/>
        <ToastMessage 
            {...toastMessage}
            toggleHidden={() => setToastMessage(prev => ({ ...prev, hidden: true }))}/>
        <Preloader {...loadingRequest.value}/>
        <BackToView to={ABSOLUTE_PATHS.SETTINGS} text="Volver a Configuración"/>
        <Container>
            <Row>
                <DataHeaderDetail 
                    icon={<PeopleIcon/>} 
                    text="Administrador de Roles"
                    width="300px"/>
                <CustomButton 
                    icon={<AddingPersonIcon/>} 
                    text="Asignar nuevo rol"
                    size="medium"
                    onClick={() => openForm()}/>
            </Row>
            <HeaderRoles 
                filterFields={{
                    value: filterFields,
                    set: setFilterFields,
                    isCleared: checkClearedFilterFields,
                    clear: clearFilterFields
                }}
                searchedFilters={searchedFilters}
                paginator={paginator}
                data={dataRelatedToRoles}
                setTriggerRequest={userList.triggerFillRequest}
                clearNewlyAddedOrUpdatedUser={clearNewlyAddedOrUpdatedUser}/>
            <CustomTableList 
                minWidth="900px" 
                maxHeight="50vh"
                scrollColor="secondary"
                headers={HEADERS_TABLE_LIST}>
                {userList.value?.map((item, idx) => (
                    <TableListItem 
                        key={idx} 
                        {...item} 
                        newlyAddedOrUpdatedUser={userList.newlyAddedOrUpdatedUser.value}
                        filterFields={filterFields}
                        showForm={showForm}
                        currentUser={currentUser}
                        openForm={openForm}
                        openDialogForDeleteUser={openDialogForDeleteUser}/>
                ))}
            </CustomTableList>
        </Container>
        <FormRole 
            show={showForm} 
            toggleShow={toggleShowForm}
            closeForm={closeForm}
            currentUser={currentUser}
            loadingRequest={loadingRequest}
            data={dataRelatedToRoles}
            userList={userList}/>
        {currentUser && 
        <DialogAlertAction
            minWidth="560px"
            widthContent={"90%"}
            spacingContainer="16px"
            variant="secondary"
            show={showDialogDeleteUser}
            toggleShow={toggleShowDialogDeleteUser}
            title="¿Deseas eliminar este usuario?"
            contentComponent={
                <p className="paragraph">
                    Recuerda que el usuario <b>{currentUser.user}</b> será <b>Eliminado</b> y perderá su rol.
                </p>}>
            <CustomButton
                variant="secondary"
                size="medium"
                text="No, cancelar"
                width="110px"
                onClick={() => toggleShowDialogDeleteUser(false)}/>
            <CustomButton
                size="medium"
                text="Sí, eliminar"
                width="110px"
                onClick={deleteUser}/>
        </DialogAlertAction>}
        </>
    );
}

export default SettingsRoleAdmin;